import React from 'react';
import { Button, Grid, Paper, TextField } from 'components/mui';
import CheckpointEditor from './checkpointEditor/CheckpointEditor';
import styles from './ChecklistEditor.module.css';
import useChecklistEditor from '../../hooks/useChecklistEditor';
import CheckpointTable from './checkpointTable/CheckpointTable';
import LoadingBackdrop from 'components/LoadingBackdrop';
import ChecklistAlertTypes from '../../constants/checklistAlertTypes';
import ChecklistUnsavedWarning from '../AlertDialog/ChecklistUnsavedWarning';
import ChecklistAssignedPatrolWarning from '../AlertDialog/ChecklistAssignedPatrolWarning';
import ChecklistAssignedPatrolInfo from '../AlertDialog/ChecklistAssignedPatrolInfo';
import AssignedPatrolButton from '../../utils/AssignedPatrolButton';
import { ChecklistInputFields } from '../../constants/checklistInputTypes';
import ChecklistTypes from '../../constants/checklistTypes';
import PreviewIncidentReportButton from '../../utils/PreviewIncidentReportButton';

// WHEN SHOULD UNSAVED CHANGES BE TRIGGERED:
// 1. WHEN CHECKLIST NAME OR DESCRIPTION IS CHANGED;
// 2. WHEN CHECKPOINTS STATE IS CHANGED

const ChecklistEditor = ({
	checklist,
	setChecklists,
	checklistType,
	customerID,
	addressID,
	setOpenInEditMode,
	setShowAssignedPatrols,
	saveRef,
}) => {
	const state = useChecklistEditor(
		checklist,
		setChecklists,
		checklistType,
		customerID,
		addressID,
		setOpenInEditMode,
		saveRef,
	);

	return (
		<>
			<Grid container className={styles.container}>
				<Paper className={styles.sub_container} elevation={1}>
					{!checklist && (
						<h2 className={styles.main_title}>
							New {checklistType} Checklist:
						</h2>
					)}
					<Grid item xs={12} className={styles.wrap_main_categories}>
						<TextField
							className={styles.wrap_textFields_col}
							required
							name={ChecklistInputFields.Name}
							label="Name"
							variant="outlined"
							InputLabelProps={{ shrink: true }}
							error={
								state.inputValidation[ChecklistInputFields.Name]
									.isError
							}
							helperText={
								state.inputValidation[ChecklistInputFields.Name]
									.isError &&
								state.inputValidation[ChecklistInputFields.Name]
									.errorMessage
							}
							size="small"
							value={
								state.checklistFields[ChecklistInputFields.Name]
							}
							onChange={state.onHandleInput}
						/>
						<TextField
							className={styles.wrap_textFields_col}
							label="Description"
							name={ChecklistInputFields.Description}
							size="small"
							variant="outlined"
							placeholder="Only visible to the dispatcher"
							InputLabelProps={{ shrink: true }}
							value={
								state.checklistFields[
									ChecklistInputFields.Description
								]
							}
							onChange={state.onHandleInput}
						/>
						{checklist &&
							checklist.Type === ChecklistTypes.Patrol && (
								<div className={styles.info_button}>
									<AssignedPatrolButton
										numberOfAssignedPatrols={
											checklist.AssignedPatrols.length
										}
										setShowAssignedPatrols={
											setShowAssignedPatrols
										}
									/>
								</div>
							)}
						{checklist &&
							checklist.Type === ChecklistTypes.Incident && (
								<div className={styles.info_button}>
									<PreviewIncidentReportButton
										checklistID={checklist.ChecklistID}
									/>
								</div>
							)}
					</Grid>

					<Grid container>
						<Grid item xs={6}>
							<Paper elevation={3}>
								<CheckpointTable
									checkpoints={state.checkpoints}
									setCheckpoints={state.setCheckpoints}
									checklist={state.checklist}
									highlightedCheckpointID={
										state.highlightedCheckpointID
									}
									setHighlightedCheckpointID={
										state.setHighlightedCheckpointID
									}
									displayCheckpointTableMessage={
										state.displayCheckpointTableMessage
									}
									inputValidationHelpers={
										state.inputValidationHelpers
									}
								/>
							</Paper>
						</Grid>
						<Grid item xs={6}>
							<Paper elevation={3}>
								<CheckpointEditor
									highlightedCheckpoint={state.getHighlightedCheckpoint()}
									setCheckpoints={state.setCheckpoints}
									inputValidation={state.inputValidation}
									setInputValidation={
										state.setInputValidation
									}
								/>
							</Paper>
						</Grid>
					</Grid>
					<Paper className={styles.btn_container} elevation={3}>
						<Button
							variant="contained"
							color="sds"
							className={styles.button}
							onClick={state.onSaveChecklist}
						>
							Save
						</Button>
						<Button
							variant="contained"
							color="sds"
							className={styles.button}
							onClick={state.onResetChecklist}
						>
							Reset
						</Button>
					</Paper>
				</Paper>
			</Grid>
			{state.alertType === ChecklistAlertTypes.unsavedWarning && (
				<ChecklistUnsavedWarning
					handler={
						state.alertTypesHandlers[
							ChecklistAlertTypes.unsavedWarning
						]
					}
				/>
			)}
			{state.alertType === ChecklistAlertTypes.assignedPatrolWarning && (
				<ChecklistAssignedPatrolWarning
					numberOfAssignedPatrols={checklist?.AssignedPatrols.length}
					handler={
						state.alertTypesHandlers[
							ChecklistAlertTypes.assignedPatrolWarning
						]
					}
				/>
			)}
			{state.alertType === ChecklistAlertTypes.assignedPatrolInfo && (
				<ChecklistAssignedPatrolInfo
					patrols={checklist.AssignedPatrols}
					handler={
						state.alertTypesHandlers[
							ChecklistAlertTypes.assignedPatrolInfo
						]
					}
				/>
			)}
			<LoadingBackdrop open={state.pending} />
		</>
	);
};
export default ChecklistEditor;
